import React from 'react';

// All bars as ONE path: a 2-wide bar with rx=1 is exactly a 2-wide stroke with
// round caps, shortened by the cap at each end. 260 <rect>s per waveform made
// the feed ~7k elements, and every restyle and layout paid for each of them.
export const barsPath = (heights, H, barW, gap) => heights.map((barH, i) => {
    const x = i * (barW + gap) + barW / 2;
    const cap = Math.min(barW / 2, barH / 2);
    return `M${x} ${((H - barH) / 2 + cap).toFixed(2)}V${((H + barH) / 2 - cap).toFixed(2)}`;
}).join('');

// Fake waveform for TRACKS (For You → Tracks/Trending, Liked tracks). Unlike the mix fake —
// which is deliberately flat (mixes are loudness-maxed) — a single track has
// musical structure, so this draws a far more DETAILED, dynamic silhouette:
// intro/build/drop/breakdown envelope, fine bar-to-bar variation, and the
// occasional transient spike (kick/snare hits). barWidth/gap match the real
// FeedWaveform so the swap-in on play is seamless. Seeded so each card differs.
// Shown only in FEED_WAVEFORM_MODE=FAKE; replaced by the real waveform on play.
const FakeTrackWaveform = React.memo(({ seed = 0 }) => {
    const BARS = 260;
    const H = 60;
    const barW = 2;
    const gap = 2;
    const width = BARS * (barW + gap);

    // Seeded PRNG (LCG) — same generator as the mix fake. Two extra warm-up
    // pulls so neighbouring seeds (index+1, index+2 …) don't start in lock-step
    // — without this the first few derived values track each other and every
    // card's envelope comes out near-identical.
    let s = (seed * 9301 + 49297) % 233280;
    const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
    rand(); rand();

    // Build a randomised set of sections (intro / build / drop / breakdown /
    // outro-style). Everything that shapes the silhouette is randomised per
    // seed so no two cards share the same structure: how MANY sections there
    // are, where each boundary falls, and each section's loudness floor. This
    // is what stops the loud "drop" from always landing at the same point.
    const sectionCount = 4 + Math.floor(rand() * 4); // 4–7 sections
    // Random boundary positions across [0,1], sorted — gives uneven section
    // widths instead of fixed 1/N slices.
    const bounds = [];
    for (let k = 0; k < sectionCount - 1; k++) bounds.push(rand());
    bounds.sort((a, b) => a - b);
    const edges = [0, ...bounds, 1];
    // Each section gets its own floor in [0.30, 0.97]. Floors stay clear of
    // zero so even "quiet" sections keep a visible body like a real track.
    const sectionFloors = [];
    for (let k = 0; k < sectionCount; k++) sectionFloors.push(0.30 + rand() * 0.67);
    const floorAt = (t) => {
        for (let k = 0; k < sectionCount; k++) {
            if (t >= edges[k] && t < edges[k + 1]) return sectionFloors[k];
        }
        return sectionFloors[sectionCount - 1];
    };

    // Randomise the envelope sine frequencies + phases too, so the fine ripple
    // differs card to card rather than sharing one fixed wobble.
    const phaseA = rand() * Math.PI * 2;
    const phaseB = rand() * Math.PI * 2;
    const phaseC = rand() * Math.PI * 2;
    const freqA = 4 + rand() * 5;   // slow swell
    const freqB = 12 + rand() * 10; // mid ripple
    const freqC = 26 + rand() * 16; // fine detail
    const bars = [];
    for (let i = 0; i < BARS; i++) {
        const t = i / BARS;
        const floor = floorAt(t);
        // Layered sines give a richer envelope than the mix's two-term version.
        const env = floor
            + 0.10 * Math.sin(t * Math.PI * freqA + phaseA)
            + 0.06 * Math.sin(t * Math.PI * freqB + phaseB)
            + 0.04 * Math.sin(t * Math.PI * freqC + phaseC);
        // Strong per-bar noise → the fine, detailed look of a real track.
        let h = env + (rand() - 0.5) * 0.30;
        // Sparse transient spikes (drum hits) poking above the envelope.
        if (rand() > 0.94) h += 0.18;
        h = Math.max(0.18, Math.min(1.0, h));
        bars.push(h);
    }

    return (
        <svg
            className="feed-fake-waveform feed-fake-waveform-track"
            viewBox={`0 0 ${width} ${H}`}
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={`trackwave-${seed}`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={H}>
                    <stop offset="0%" stopColor="rgba(255,255,255,0.75)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.5)" />
                </linearGradient>
            </defs>
            <path d={barsPath(bars.map((h) => Math.max(1, h * H)), H, barW, gap)} stroke={`url(#trackwave-${seed})`} strokeWidth={barW} strokeLinecap="round" fill="none" />
        </svg>
    );
});

export default FakeTrackWaveform;
